import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CardDeck } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { DeckCard } from "@/components/ui/deck-card";
import { toast } from "@/hooks/use-toast";
import PullToRefresh from "react-simple-pull-to-refresh";

export default function SavedDecksPage() {
  const [_, setLocation] = useLocation();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: decks, isLoading } = useQuery<CardDeck[]>({
    queryKey: ["user-decks"],
    queryFn: async () => {
      const response = await fetch("/api/deck/my-decks", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch saved decks");
      }
      return response.json();
    },
  });

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["user-decks"] });
    return Promise.resolve();
  };

  return (
    <PullToRefresh 
      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing...</p>
        </div>
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => setLocation("/user-settings")}
            variant="ghost"
            size="icon"
            className="hover:bg-white/10"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-2xl font-bold">Saved Decks</h1>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-10 w-10 animate-spin text-primary" />
            <p className="mt-4 text-sm text-white/60">Loading decks...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {decks?.map((deck) => <DeckCard key={deck.id} deck={deck?.deck} />)}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
